import React, { useState, useMemo } from "react";
import {
    Box, Typography, Drawer, IconButton, Button, Chip, Tabs, Tab,
    Stack, Avatar, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TableSortLabel, Pagination, Divider,
    useMediaQuery, useTheme,
} from "@mui/material";
import {
    Close, Compare, PhoneEnabled, PhoneMissed, PhoneInTalk,
    AccessTime, TrendingUp, EmojiEvents, CheckCircle,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import {
    Chart as ChartJS,
    ArcElement, CategoryScale, LinearScale, BarElement, PointElement,
    LineElement, Filler, Tooltip as CJTooltip, Legend,
} from "chart.js";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import SMComparisonPanel from "./SMComparisonPanel";

ChartJS.register(
    ArcElement, CategoryScale, LinearScale, BarElement,
    PointElement, LineElement, Filler, CJTooltip, Legend
);

/* ── Design tokens (aligned with app primary palette) ── */
const SM = {
    sans:       "'Inter', system-ui, sans-serif",
    teal:       "#4338ca", tealSoft:  "#eef2ff", tealBd:   "#c7d2fe",  // indigo primary
    coral:      "#f97316", coralSoft: "#fff7ed", coralBd:  "#fed7aa",  // orange accent
    purple:     "#7c3aed", purpleSoft:"#f5f3ff", purpleBd: "#ddd6fe",
    cyan:       "#0284c7", cyanSoft:  "#f0f9ff", cyanBd:   "#bae6fd",  // sky blue
    green:      "#16a34a", greenSoft: "#f0fdf4", greenBd:  "#bbf7d0",
    red:        "#dc2626", redSoft:   "#fef2f2", redBd:    "#fecaca",
    amber:      "#d97706", amberSoft: "#fffbeb", amberBd:  "#fde68a",
    page:       "#f8fafc", white:     "#ffffff",
    border:     "#e2e8f0", border2:   "#f1f5f9",
    txt1:       "#0f172a", txt2: "#334155", txt3: "#64748b", txt4: "#94a3b8",
};

const CHART_COLORS = ["#4338ca", "#f97316", "#7c3aed", "#0284c7", "#ec4899", "#d97706", "#16a34a"];

/* ── Helpers ── */
const fmtDur   = s => { if (!s) return "—"; const m = Math.floor(s / 60), sec = Math.floor(s % 60); return m ? `${m}m ${sec}s` : `${sec}s`; };
const pct      = (a, b) => b > 0 ? Math.round((a / b) * 100) : 0;
const AV_COLORS = ["#4338ca","#f97316","#7c3aed","#0284c7","#ec4899","#d97706"];
const avColor  = n => { if (!n) return SM.txt4; let h = 0; for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffffffff; return AV_COLORS[Math.abs(h) % AV_COLORS.length]; };
const initials = n => { if (!n) return "?"; const p = n.trim().split(" "); return (p.length >= 2 ? p[0][0] + p[1][0] : p[0].slice(0, 2)).toUpperCase(); };
const scoreColor = n => n >= 70 ? SM.green : n >= 40 ? SM.amber : SM.red;

/* ── Styled components ── */
const DrawerTabs = styled(Tabs)({
    borderBottom: `1px solid ${SM.border}`,
    minHeight: 44,
    "& .MuiTabs-indicator": { backgroundColor: SM.teal, height: 3, borderRadius: "3px 3px 0 0" },
    "& .MuiTab-root": {
        fontFamily: SM.sans, fontSize: 12, fontWeight: 600,
        textTransform: "none", minHeight: 44, minWidth: 0, padding: "6px 14px",
        color: SM.txt3,
        "&.Mui-selected": { color: SM.teal },
    },
});

const MiniKpiBox = styled(Box)({
    background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 10,
    padding: "10px 14px", textAlign: "center", flex: 1,
});

const baseChartOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { titleFont: { family: SM.sans }, bodyFont: { family: SM.sans } } },
};

/* ════════════════════════════════════════════════
   OVERVIEW TAB
════════════════════════════════════════════════ */
function OverviewTab({ sm }) {
    const doughnutData = {
        labels: ["Answered", "Missed", "Not Answered"],
        datasets: [{
            data: [sm.answered, sm.missed, sm.notAnswered],
            backgroundColor: [SM.teal, SM.coral, SM.amber],
            borderWidth: 2, borderColor: SM.white,
        }],
    };
    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const barData = {
        labels: dayLabels,
        datasets: [{
            label: "Calls",
            data: dayLabels.map(d => sm.callsByDay?.[d] ?? 0),
            backgroundColor: alpha(SM.teal, 0.75), borderRadius: 6, borderSkipped: false,
        }],
    };
    const recent = (sm.rawLogs || []).slice().sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0)).slice(0, 5);

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2, width: "100%", boxSizing: "border-box" }}>
            {/* Mini KPI row */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {[
                    { label: "Total Calls",   value: sm.totalCalls,  color: SM.teal },
                    { label: "Answered",       value: sm.answered,    color: SM.green },
                    { label: "Answer Rate",    value: `${pct(sm.answered, sm.totalCalls)}%`, color: SM.teal },
                    { label: "Avg Duration",   value: fmtDur(sm.avgDurationSec), color: SM.purple },
                ].map(k => (
                    <MiniKpiBox key={k.label} sx={{ minWidth: { xs: 0, sm: 70 }, flexBasis: { xs: "calc(50% - 6px)", sm: "auto" } }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: k.color, fontFamily: SM.sans }}>
                            {k.value}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: SM.txt3, fontFamily: SM.sans, mt: 0.25 }}>
                            {k.label}
                        </Typography>
                    </MiniKpiBox>
                ))}
            </Stack>

            {/* Charts row */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
                <Box sx={{ flex: 1, background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Call Status</Typography>
                    <Box sx={{ height: { xs: 180, sm: 220 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Box sx={{ width: "100%", maxWidth: 200, height: "100%" }}>
                            <Doughnut data={doughnutData} options={{ ...baseChartOpts, cutout: "65%", plugins: { ...baseChartOpts.plugins, legend: { display: true, position: "bottom", labels: { font: { family: SM.sans, size: 11 }, boxWidth: 12, padding: 10 } } } }} />
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ flex: 1.4, background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Calls by Day</Typography>
                    <Box sx={{ height: { xs: 160, sm: 200 } }}>
                        <Bar data={barData} options={{ ...baseChartOpts, scales: { x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 } } }, y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } } } }} />
                    </Box>
                </Box>
            </Stack>

            {/* Recent calls */}
            <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1 }}>Recent Calls</Typography>
                <Stack spacing={0.75}>
                    {recent.length === 0 ? (
                        <Typography sx={{ fontSize: 12, color: SM.txt4, fontFamily: SM.sans }}>No calls in this period.</Typography>
                    ) : recent.map((r, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.25, borderRadius: 8, border: `1px solid ${SM.border2}`, background: SM.page }}>
                            <Chip label={r.callStatus || "—"} size="small" sx={{ fontSize: 10, fontFamily: SM.sans, height: 20, backgroundColor: r.callStatus === "answered" ? SM.greenSoft : r.callStatus === "missed" ? SM.redSoft : SM.amberSoft, color: r.callStatus === "answered" ? SM.green : r.callStatus === "missed" ? SM.red : SM.amber }} />
                            <Typography sx={{ fontSize: 10, color: SM.txt2, fontFamily: SM.sans, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {r.customerName || r.customerNumber || "Unknown"}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: SM.txt4, fontFamily: SM.sans }}>
                                {fmtDur(r.durationSec)}
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>
                                {r.startedAt ? new Date(r.startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}

/* ════════════════════════════════════════════════
   TIMELINE TAB
════════════════════════════════════════════════ */
function TimelineTab({ sm }) {
    const hours = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
    const hourData = sm.callsByHour || Array(24).fill(0);
    const maxH = Math.max(...hourData, 1);

    const dailyMap = useMemo(() => {
        const m = {};
        (sm.rawLogs || []).forEach(r => {
            const d = r.startedAt ? new Date(r.startedAt).toLocaleDateString("en-CA") : null;
            if (d) m[d] = (m[d] || 0) + 1;
        });
        return m;
    }, [sm.rawLogs]);
    const sortedDays = Object.keys(dailyMap).sort();
    const lineData = {
        labels: sortedDays.map(d => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
        datasets: [{
            label: "Calls",
            data: sortedDays.map(d => dailyMap[d]),
            borderColor: SM.teal, backgroundColor: alpha(SM.teal, 0.1),
            fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: SM.teal,
        }],
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2.5, width: "100%", boxSizing: "border-box" }}>
            <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Calls by Hour of Day</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: "100%" }}>
                    {hours.map((h, i) => {
                        const val = hourData[i] || 0;
                        const intensity = maxH > 0 ? val / maxH : 0;
                        const bg = intensity === 0 ? SM.border2 : alpha(SM.teal, 0.15 + intensity * 0.75);
                        return (
                            <Box key={h} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                                <Box title={`${h}: ${val} calls`} sx={{ width: { xs: 22, sm: 28 }, height: { xs: 28, sm: 36 }, borderRadius: 1, background: bg, cursor: "default", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {val > 0 && <Typography sx={{ fontSize: 9, fontWeight: 700, color: intensity > 0.5 ? SM.white : SM.txt2, fontFamily: SM.sans }}>{val}</Typography>}
                                </Box>
                                <Typography sx={{ fontSize: 8, color: SM.txt4, fontFamily: SM.sans }}>{i % 3 === 0 ? h.slice(0, 2) : ""}</Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>Low</Typography>
                    {[0.1, 0.3, 0.5, 0.7, 0.9].map(v => (
                        <Box key={v} sx={{ width: 14, height: 14, borderRadius: 1, background: alpha(SM.teal, v) }} />
                    ))}
                    <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>High</Typography>
                </Stack>
            </Box>

            <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Daily Call Trend</Typography>
                <Box sx={{ height: { xs: 180, sm: 220 } }}>
                    {sortedDays.length > 0 ? (
                        <Line data={lineData} options={{ ...baseChartOpts, scales: { x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 }, maxRotation: 45 } }, y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } } } }} />
                    ) : (
                        <Typography sx={{ fontSize: 12, color: SM.txt4, fontFamily: SM.sans, textAlign: "center", pt: 4 }}>No trend data available.</Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

/* ════════════════════════════════════════════════
   STATUS ANALYSIS TAB
════════════════════════════════════════════════ */
function StatusTab({ sm }) {
    const statusBar = {
        labels: ["Status Breakdown"],
        datasets: [
            { label: "Answered",     data: [sm.answered],    backgroundColor: SM.teal,  borderRadius: 4 },
            { label: "Missed",       data: [sm.missed],      backgroundColor: SM.coral, borderRadius: 4 },
            { label: "Not Answered", data: [sm.notAnswered], backgroundColor: SM.amber, borderRadius: 4 },
        ],
    };
    const dirData = sm.directionDist || {};
    const dirBar = {
        labels: Object.keys(dirData),
        datasets: [{
            label: "Calls",
            data: Object.values(dirData),
            backgroundColor: [SM.teal, SM.coral, SM.purple, SM.cyan].slice(0, Object.keys(dirData).length),
            borderRadius: 6,
        }],
    };

    const stats = [
        { label: "Answered",     value: sm.answered,    color: SM.green,  pct: pct(sm.answered, sm.totalCalls) },
        { label: "Missed",       value: sm.missed,      color: SM.red,    pct: pct(sm.missed, sm.totalCalls) },
        { label: "Not Answered", value: sm.notAnswered, color: SM.amber,  pct: pct(sm.notAnswered, sm.totalCalls) },
        { label: "Connected",    value: sm.connected,   color: SM.teal,   pct: pct(sm.connected, sm.totalCalls) },
    ];

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2, width: "100%", boxSizing: "border-box" }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {stats.map(s => (
                    <MiniKpiBox key={s.label} sx={{ minWidth: { xs: 0, sm: 80 }, flexBasis: { xs: "calc(50% - 6px)", sm: "auto" } }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.color, fontFamily: SM.sans }}>{s.value}</Typography>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: SM.txt3, fontFamily: SM.sans }}>{s.pct}%</Typography>
                        <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>{s.label}</Typography>
                    </MiniKpiBox>
                ))}
            </Stack>

            <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Call Status Stacked</Typography>
                <Box sx={{ height: { xs: 120, sm: 150 } }}>
                    <Bar data={statusBar} options={{ ...baseChartOpts, indexAxis: "y", scales: { x: { stacked: true, grid: { color: SM.border2 }, ticks: { font: { family: SM.sans, size: 10 } } }, y: { stacked: true, display: false } }, plugins: { ...baseChartOpts.plugins, legend: { display: true, position: "bottom", labels: { font: { family: SM.sans, size: 11 }, boxWidth: 12, padding: 10 } } } }} />
                </Box>
            </Box>

            {Object.keys(dirData).length > 0 && (
                <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Call Direction</Typography>
                    <Box sx={{ height: { xs: 140, sm: 170 } }}>
                        <Bar data={dirBar} options={{ ...baseChartOpts, scales: { x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 } } }, y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } } } }} />
                    </Box>
                </Box>
            )}
        </Box>
    );
}

/* ════════════════════════════════════════════════
   DURATION ANALYSIS TAB
════════════════════════════════════════════════ */
function DurationTab({ sm }) {
    const logs = sm.rawLogs || [];

    const buckets = { "0–30s": 0, "31–120s": 0, "2–5m": 0, "5m+": 0 };
    logs.forEach(r => {
        const d = r.durationSec || 0;
        if (d <= 30) buckets["0–30s"]++;
        else if (d <= 120) buckets["31–120s"]++;
        else if (d <= 300) buckets["2–5m"]++;
        else buckets["5m+"]++;
    });

    const bucketBar = {
        labels: Object.keys(buckets),
        datasets: [{ label: "Calls", data: Object.values(buckets), backgroundColor: [SM.teal, SM.cyan, SM.purple, SM.coral], borderRadius: 6 }],
    };

    const dayDurMap = useMemo(() => {
        const m = {};
        logs.forEach(r => {
            const d = r.startedAt ? new Date(r.startedAt).toLocaleDateString("en-CA") : null;
            if (d) { if (!m[d]) m[d] = []; m[d].push(r.durationSec || 0); }
        });
        return m;
    }, [logs]);
    const sortedD = Object.keys(dayDurMap).sort();
    const lineData = {
        labels: sortedD.map(d => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" })),
        datasets: [{
            label: "Avg Duration (s)",
            data: sortedD.map(d => Math.round(dayDurMap[d].reduce((a, b) => a + b, 0) / dayDurMap[d].length)),
            borderColor: SM.purple, backgroundColor: alpha(SM.purple, 0.1),
            fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: SM.purple,
        }],
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2, width: "100%", boxSizing: "border-box" }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {[
                    { label: "Avg Duration",     value: fmtDur(sm.avgDurationSec) },
                    { label: "Avg Bill Duration", value: fmtDur(sm.avgBillSec) },
                    { label: "Total Talk Time",   value: fmtDur(sm.totalDurationSec) },
                    { label: "Total Calls",       value: sm.totalCalls },
                ].map(k => (
                    <MiniKpiBox key={k.label} sx={{ minWidth: { xs: 0, sm: 80 }, flexBasis: { xs: "calc(50% - 6px)", sm: "auto" } }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800, color: SM.purple, fontFamily: SM.sans }}>{k.value}</Typography>
                        <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans, mt: 0.25 }}>{k.label}</Typography>
                    </MiniKpiBox>
                ))}
            </Stack>

            <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Duration Buckets</Typography>
                <Box sx={{ height: { xs: 160, sm: 200 } }}>
                    <Bar data={bucketBar} options={{ ...baseChartOpts, scales: { x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 } } }, y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } } } }} />
                </Box>
            </Box>

            {sortedD.length > 1 && (
                <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Avg Duration Trend</Typography>
                    <Box sx={{ height: { xs: 160, sm: 200 } }}>
                        <Line data={lineData} options={{ ...baseChartOpts, scales: { x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 }, maxRotation: 45 } }, y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } } } }} />
                    </Box>
                </Box>
            )}
        </Box>
    );
}

/* ════════════════════════════════════════════════
   LEAD ANALYSIS TAB
════════════════════════════════════════════════ */
function LeadTab({ sm }) {
    const tempDist = sm.leadTempDist || {};
    const tempKeys = Object.keys(tempDist).filter(k => tempDist[k] > 0);
    const tempColors = { Hot: SM.coral, Warm: SM.amber, Cold: SM.cyan };
    const doughnutData = {
        labels: tempKeys,
        datasets: [{
            data: tempKeys.map(k => tempDist[k]),
            backgroundColor: tempKeys.map(k => tempColors[k] || SM.teal),
            borderWidth: 2, borderColor: SM.white,
        }],
    };

    const score = Math.round(sm.avgLeadScore || 0);
    const scorePct = Math.min(100, score);
    const scoreC = score >= 70 ? SM.green : score >= 40 ? SM.amber : SM.red;

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2, width: "100%", boxSizing: "border-box" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap>
                <Box sx={{ flex: 1, background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Lead Temperature</Typography>
                    {tempKeys.length > 0 ? (
                        <Box sx={{ height: { xs: 180, sm: 200 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <Box sx={{ width: "100%", maxWidth: 200, height: "100%" }}>
                                <Doughnut data={doughnutData} options={{ ...baseChartOpts, cutout: "65%", plugins: { ...baseChartOpts.plugins, legend: { display: true, position: "bottom", labels: { font: { family: SM.sans, size: 11 }, boxWidth: 12, padding: 8 } } } }} />
                            </Box>
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: 12, color: SM.txt4, fontFamily: SM.sans, textAlign: "center", py: 4 }}>No lead temperature data.</Typography>
                    )}
                </Box>

                <Box sx={{ flex: 1, background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans }}>Avg Lead Score</Typography>
                    <Box sx={{ position: "relative", width: 120, height: 120 }}>
                        <svg viewBox="0 0 120 120" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                            <circle cx="60" cy="60" r="50" fill="none" stroke={SM.border2} strokeWidth="12" />
                            <circle cx="60" cy="60" r="50" fill="none" stroke={scoreC} strokeWidth="12"
                                strokeDasharray={`${(scorePct / 100) * 314} 314`}
                                strokeLinecap="round" />
                        </svg>
                        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <Typography sx={{ fontSize: 26, fontWeight: 800, color: scoreC, fontFamily: SM.sans, lineHeight: 1 }}>{score}</Typography>
                            <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans }}>/ 100</Typography>
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                        {tempKeys.map(k => (
                            <Chip key={k} label={`${k}: ${tempDist[k]}`} size="small" sx={{ fontSize: 11, fontFamily: SM.sans, height: 22, background: alpha(tempColors[k] || SM.teal, 0.12), color: tempColors[k] || SM.teal }} />
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}

/* ════════════════════════════════════════════════
   AI ANALYSIS TAB
════════════════════════════════════════════════ */
function AITab({ sm }) {
    const callTypeDist = sm.callTypeDist || {};
    const ctKeys = Object.keys(callTypeDist);
    const ctBar = {
        labels: ctKeys,
        datasets: [{
            label: "Calls",
            data: ctKeys.map(k => callTypeDist[k]),
            backgroundColor: CHART_COLORS.slice(0, ctKeys.length),
            borderRadius: 6,
        }],
    };

    const tempDist = sm.leadTempDist || {};
    const tempKeys = Object.keys(tempDist).filter(k => tempDist[k] > 0);
    const tempColors = { Hot: SM.coral, Warm: SM.amber, Cold: SM.cyan };

    const aiInsights = useMemo(() => {
        const counts = {};
        (sm.rawLogs || []).forEach(r => {
            if (r.aiInsight) { counts[r.aiInsight] = (counts[r.aiInsight] || 0) + 1; }
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5);
    }, [sm.rawLogs]);

    return (
        <Box sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", flexDirection: "column", gap: 2, width: "100%", boxSizing: "border-box" }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <MiniKpiBox sx={{ minWidth: { xs: 0, sm: 90 }, flexBasis: { xs: "calc(50% - 6px)", sm: "auto" } }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: scoreColor(sm.avgLeadScore || 0), fontFamily: SM.sans }}>
                        {Math.round(sm.avgLeadScore || 0)}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans, mt: 0.25 }}>Avg AI Score</Typography>
                </MiniKpiBox>
                <MiniKpiBox sx={{ minWidth: { xs: 0, sm: 90 }, flexBasis: { xs: "calc(50% - 6px)", sm: "auto" } }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: SM.teal, fontFamily: SM.sans }}>
                        {Math.round(sm.performanceScore || 0)}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans, mt: 0.25 }}>Perf Score</Typography>
                </MiniKpiBox>
                {tempKeys.map(k => (
                    <MiniKpiBox key={k} sx={{ minWidth: { xs: 0, sm: 70 }, flexBasis: { xs: "calc(50% - 6px)", sm: "auto" } }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: tempColors[k] || SM.teal, fontFamily: SM.sans }}>{tempDist[k]}</Typography>
                        <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans, mt: 0.25 }}>{k} Leads</Typography>
                    </MiniKpiBox>
                ))}
            </Stack>

            {ctKeys.length > 0 && (
                <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>AI Call Type Breakdown</Typography>
                    <Box sx={{ height: { xs: 150, sm: 190 } }}>
                        <Bar data={ctBar} options={{ ...baseChartOpts, scales: { x: { grid: { display: false }, ticks: { font: { family: SM.sans, size: 10 } } }, y: { ticks: { font: { family: SM.sans, size: 10 } }, grid: { color: SM.border2 } } } }} />
                    </Box>
                </Box>
            )}

            {aiInsights.length > 0 && (
                <Box sx={{ background: SM.white, border: `1px solid ${SM.border}`, borderRadius: 12, p: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, mb: 1.5 }}>Top AI Insights</Typography>
                    <Stack spacing={0.75}>
                        {aiInsights.map(([insight, count], i) => (
                            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.25, borderRadius: 8, background: SM.page, border: `1px solid ${SM.border2}` }}>
                                <Chip label={count} size="small" sx={{ fontSize: 10, fontFamily: SM.sans, height: 20, background: alpha(SM.teal, 0.12), color: SM.teal, fontWeight: 700, minWidth: 28 }} />
                                <Typography sx={{ fontSize: 12, color: SM.txt2, fontFamily: SM.sans }}>{insight}</Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}

            {aiInsights.length === 0 && ctKeys.length === 0 && (
                <Typography sx={{ fontSize: 12, color: SM.txt4, fontFamily: SM.sans, textAlign: "center", py: 3 }}>
                    No AI analysis data available for this period.
                </Typography>
            )}
        </Box>
    );
}

/* ════════════════════════════════════════════════
   RAW LOGS TAB
════════════════════════════════════════════════ */
function RawLogsTab({ sm }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("startedAt");
    const [sortDir, setSortDir] = useState("desc");
    const PAGE_SIZE = 15;

    const logs = sm.rawLogs || [];
    const sorted = useMemo(() => {
        return [...logs].sort((a, b) => {
            const av = a[sortBy] ?? 0, bv = b[sortBy] ?? 0;
            return sortDir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
        });
    }, [logs, sortBy, sortDir]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const pageData = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleSort = col => {
        if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortBy(col); setSortDir("desc"); }
    };

    const statusColor = s => s === "answered" ? SM.green : s === "missed" ? SM.red : SM.amber;
    const statusBg    = s => s === "answered" ? SM.greenSoft : s === "missed" ? SM.redSoft : SM.amberSoft;

    if (isMobile) {
        return (
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1, width: "100%", boxSizing: "border-box" }}>
                {pageData.map((r, i) => (
                    <Box key={i} sx={{ p: 1.5, borderRadius: 10, border: `1px solid ${SM.border}`, background: SM.white }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" useFlexGap sx={{ gap: 1 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: SM.txt1, fontFamily: SM.sans, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {r.customerName || r.customerNumber || "Unknown"}
                            </Typography>
                            <Chip label={r.callStatus || "—"} size="small" sx={{ fontSize: 10, fontFamily: SM.sans, height: 20, background: statusBg(r.callStatus), color: statusColor(r.callStatus), flexShrink: 0 }} />
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                            <Typography sx={{ fontSize: 11, color: SM.txt3, fontFamily: SM.sans }}>{r.direction || "—"}</Typography>
                            <Typography sx={{ fontSize: 11, color: SM.txt4, fontFamily: SM.sans }}>·</Typography>
                            <Typography sx={{ fontSize: 11, color: SM.txt3, fontFamily: SM.sans }}>{fmtDur(r.durationSec)}</Typography>
                            {r.aiLeadScore && <><Typography sx={{ fontSize: 11, color: SM.txt4, fontFamily: SM.sans }}>·</Typography><Typography sx={{ fontSize: 11, color: SM.teal, fontFamily: SM.sans }}>Score: {r.aiLeadScore}</Typography></>}
                            {r.aiLeadTemperature && <Chip label={r.aiLeadTemperature} size="small" sx={{ fontSize: 9, height: 16, fontFamily: SM.sans }} />}
                        </Stack>
                        <Typography sx={{ fontSize: 10, color: SM.txt4, fontFamily: SM.sans, mt: 0.5 }}>
                            {r.startedAt ? new Date(r.startedAt).toLocaleString() : "—"}
                        </Typography>
                    </Box>
                ))}
                {totalPages > 1 && <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small" sx={{ mt: 1, "& .MuiPaginationItem-root": { fontFamily: SM.sans, fontSize: 11 } }} />}
            </Box>
        );
    }

    const cols = [
        { id: "customerName", label: "Customer" },
        { id: "callStatus",   label: "Status" },
        { id: "direction",    label: "Direction" },
        { id: "durationSec",  label: "Duration" },
        { id: "aiLeadScore",  label: "Lead Score" },
        { id: "aiLeadTemperature", label: "Temperature" },
        { id: "startedAt",    label: "Time" },
    ];

    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, width: "100%", boxSizing: "border-box" }}>
            <TableContainer sx={{ borderRadius: 10, border: `1px solid ${SM.border}`, background: SM.white, overflowX: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ background: SM.page }}>
                            {cols.map(c => (
                                <TableCell key={c.id} sortDirection={sortBy === c.id ? sortDir : false}
                                    sx={{ fontSize: 11, fontWeight: 700, color: SM.txt2, fontFamily: SM.sans, py: 1.25, borderBottom: `1px solid ${SM.border}`, whiteSpace: "nowrap" }}>
                                    <TableSortLabel active={sortBy === c.id} direction={sortBy === c.id ? sortDir : "asc"} onClick={() => handleSort(c.id)}
                                        sx={{ "& .MuiTableSortLabel-icon": { fontSize: 14 }, fontFamily: SM.sans, color: `${SM.txt2} !important`, "&.Mui-active": { color: `${SM.teal} !important` } }}>
                                        {c.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageData.map((r, i) => (
                            <TableRow key={i} sx={{ "&:hover": { background: SM.page }, "&:last-child td": { border: 0 } }}>
                                <TableCell sx={{ fontSize: 12, color: SM.txt1, fontFamily: SM.sans, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {r.customerName || r.customerNumber || "—"}
                                </TableCell>
                                <TableCell>
                                    <Chip label={r.callStatus || "—"} size="small" sx={{ fontSize: 10, fontFamily: SM.sans, height: 20, background: statusBg(r.callStatus), color: statusColor(r.callStatus) }} />
                                </TableCell>
                                <TableCell sx={{ fontSize: 12, color: SM.txt3, fontFamily: SM.sans }}>{r.direction || "—"}</TableCell>
                                <TableCell sx={{ fontSize: 12, color: SM.txt3, fontFamily: SM.sans }}>{fmtDur(r.durationSec)}</TableCell>
                                <TableCell sx={{ fontSize: 12, color: SM.teal, fontFamily: SM.sans, fontWeight: 600 }}>{r.aiLeadScore ?? "—"}</TableCell>
                                <TableCell>
                                    {r.aiLeadTemperature ? (
                                        <Chip label={r.aiLeadTemperature} size="small" sx={{ fontSize: 10, fontFamily: SM.sans, height: 20 }} />
                                    ) : "—"}
                                </TableCell>
                                <TableCell sx={{ fontSize: 11, color: SM.txt4, fontFamily: SM.sans, whiteSpace: "nowrap" }}>
                                    {r.startedAt ? new Date(r.startedAt).toLocaleString() : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size="small"
                        sx={{ "& .MuiPaginationItem-root": { fontFamily: SM.sans, fontSize: 11 }, "& .Mui-selected": { background: `${alpha(SM.teal, 0.12)} !important`, color: SM.teal } }} />
                </Box>
            )}
        </Box>
    );
}

/* ════════════════════════════════════════════════
   MAIN DRAWER
════════════════════════════════════════════════ */
export default function SMDetailDrawer({ open, sm, allSmStats, overall, onClose }) {
    const [tabIndex, setTabIndex] = useState(0);
    const [compareOpen, setCompareOpen] = useState(false);

    const rankBadgeColor = r => r === 1 ? "#f59e0b" : r === 2 ? SM.txt3 : r === 3 ? "#cd7f32" : SM.border;

    const tabs = [
        { label: "Overview" }, { label: "Timeline" }, { label: "Status" },
        { label: "Duration" }, { label: "Leads" }, { label: "AI Analysis" }, { label: "Call Logs" },
    ];

    if (!sm) return null;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: "580px", md: "680px" },
                    maxWidth: "100vw",
                    background: SM.page,
                    fontFamily: SM.sans,
                    overflow: "hidden",
                },
            }}
        >
            <Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

                {/* Header */}
                <Box sx={{ background: SM.white, borderBottom: `1px solid ${SM.border}`, px: { xs: 2, sm: 2.5 }, py: 1.5, flexShrink: 0 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} useFlexGap>
                        <Stack direction="row" alignItems="center" spacing={1.25} useFlexGap sx={{ minWidth: 0, flex: 1 }}>
                            <Avatar sx={{ width: 40, height: 40, flexShrink: 0, background: avColor(sm.agentName), fontSize: 14, fontWeight: 700, fontFamily: SM.sans }}>
                                {initials(sm.agentName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: SM.txt1, fontFamily: SM.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {sm.agentName || "Unknown SM"}
                                    </Typography>
                                    {sm.rank && (
                                        <Box sx={{ width: 22, height: 22, flexShrink: 0, borderRadius: "50%", background: rankBadgeColor(sm.rank), display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <Typography sx={{ fontSize: 9, fontWeight: 800, color: sm.rank <= 3 ? SM.white : SM.txt3, fontFamily: SM.sans }}>
                                                #{sm.rank}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                                <Typography sx={{ fontSize: 11, color: SM.txt3, fontFamily: SM.sans, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {sm.totalCalls} calls · {pct(sm.answered, sm.totalCalls)}% answer rate · Score {Math.round(sm.performanceScore || 0)}
                                </Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0} useFlexGap>
                            {allSmStats?.length > 1 && (
                                <Button size="small" variant="outlined" startIcon={<Compare sx={{ fontSize: 13 }} />}
                                    onClick={() => setCompareOpen(true)}
                                    sx={{ fontSize: 11, fontFamily: SM.sans, textTransform: "none", borderColor: SM.teal, color: SM.teal, borderRadius: 6, minHeight: 30, px: 1.25, "&:hover": { background: SM.tealSoft } }}>
                                    Compare
                                </Button>
                            )}
                            <IconButton onClick={onClose} size="small" sx={{ color: SM.txt3 }}>
                                <Close sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>

                {/* Tabs */}
                <DrawerTabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} variant="scrollable" scrollButtons="auto" sx={{ flexShrink: 0 }}>
                    {tabs.map(t => <Tab key={t.label} label={t.label} />)}
                </DrawerTabs>

                {/* Tab Content */}
                <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", minWidth: 0 }}>
                    {tabIndex === 0 && <OverviewTab  sm={sm} />}
                    {tabIndex === 1 && <TimelineTab  sm={sm} />}
                    {tabIndex === 2 && <StatusTab    sm={sm} />}
                    {tabIndex === 3 && <DurationTab  sm={sm} />}
                    {tabIndex === 4 && <LeadTab      sm={sm} />}
                    {tabIndex === 5 && <AITab        sm={sm} />}
                    {tabIndex === 6 && <RawLogsTab   sm={sm} />}
                </Box>

                {/* Comparison overlay */}
                {compareOpen && (
                    <SMComparisonPanel
                        smA={sm}
                        allSmStats={allSmStats}
                        overall={overall}
                        onClose={() => setCompareOpen(false)}
                    />
                )}
            </Box>
        </Drawer>
    );
}
